import React, { useRef, useEffect, useCallback, useState } from 'react';
import {
  getFieldValue,
  withSitecoreContext,
} from '@sitecore-jss/sitecore-jss-react';
import { useMeasure } from 'react-use';
import { Section, Container, PlayPauseButton } from 'components/shared';
import styled from 'styled-components';
import breakpoints from 'utils/breakpoints';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import { scrollToSection } from 'utils/scrollTo';
import tw from 'twin.macro';
import config from 'temp/config';
import { useCountryValue } from 'stores/country';
import getEmbedUrl from 'utils/getEmbedUrl';
import useOnClickOutside from 'utils/hooks/useOnClickOutside';
import getVideoType from 'utils/getVideoType';
import {
  videoTranscriptFileUrl,
  videoTranscriptFileExtension,
} from 'utils/videoTranscriptFileUrl';
import cx from 'classnames';
import { Helmet } from '@dr.pogodin/react-helmet';

import loadable from '@loadable/component';
const IconButton = loadable(() =>
  import('components/components/atoms/IconButton'),
);
const Heading = loadable(() => import('components/components/atoms/Heading'));
const Copy = loadable(() => import('components/components/atoms/Copy'));
const Input = loadable(() => import('components/components/atoms/Input'));
const InputGroup = loadable(() =>
  import('components/components/molecules/InputGroup'),
);
const SearchBox = loadable(() =>
  import('components/components/organisms/SearchBox'),
);

const StyledSection = styled(Section)`
  min-height: 34rem;
  height: 87vh;
  background-image: url(${(props) => props.background});
  margin-bottom: 1.563rem;
  @media ${breakpoints.xs} {
    height: 90vh;
  }
  @media ${breakpoints.md} {
    margin-bottom: 3.125rem;
    height: 84vh;
  }
`;

const TranscriptContainer = styled.div`
  margin-top: -1.563rem;
  @media ${breakpoints.md} {
    margin-top: -3.125rem;
  }
`;

const Video = styled.video`
  min-height: 87vh;
  @media ${breakpoints.xs} {
    min-height: 90vh;
  }
  @media ${breakpoints.md} {
    min-height: 84vh;
  }
`;

const GradientTop = styled.div`
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.7) 10%, transparent 70%);
`;

const StyledHeading = styled(Heading)`
  text-shadow: 0 0 25px rgba(0, 0, 0), 0 0 20px rgba(0, 0, 0);
  @media ${breakpoints.lg} {
    ${tw`mb-0`}
    font-size: 2.875rem;
  }
`;
const StyledCopy = styled(Copy)`
  text-shadow: 0 0 25px rgba(0, 0, 0), 0 0 20px rgba(0, 0, 0);
`;

const Masthead = ({ fields, sitecoreContext }) => {
  const geolocation = getFieldValue(sitecoreContext, 'geolocation') || {};
  const latLng = { lat: geolocation.latitude, lng: geolocation.longitude };

  const [paused, setPaused] = useState(true);
  const [query, setQuery] = useState('');
  const [values, setValues] = useState();
  const [searchBoxOpen, setSearchBoxOpen] = useState(false);
  const [{ country }] = useCountryValue();
  const breakpoint = useBreakpoint();
  const isMobile = ['xs', 'sm'].includes(breakpoint);
  const isTablet = ['md'].includes(breakpoint);
  const isWinOS =
    typeof window !== 'undefined' && window.navigator.platform === 'Win32';
  const btnPlayPauseClass =
    'absolute mb-4 right-0 bottom-0 ' + (isWinOS ? 'mr-8' : 'mr-4');

  const title = getFieldValue(fields, 'title');
  const subtitle = getFieldValue(fields, 'subtitle');
  const image = getFieldValue(fields, 'image');
  const video = getFieldValue(fields, 'video') || {};
  const scrollToSectionField = getFieldValue(fields, 'scrollToSection');
  const placeholder = getFieldValue(fields, 'placeholder');

  const videoRef = useRef(null);

  const [ref, { width }] = useMeasure();
  const [containerRef, { width: groupWidth }] = useMeasure();

  const searchBoxRef = useRef();
  useOnClickOutside(searchBoxRef, () => {
    if (!isMobile) {
      setSearchBoxOpen(false);
    }
  });

  const background = isMobile
    ? image && image.srcMobile
      ? image.srcMobile
      : image && image.src
    : image && image.src;

  const fetchSearchBox = (country) => {
    if (!config) {
      return;
    }
    const baseUrl =
      config.sitecoreApiHost +
      '/sitecore/api/layout/render/jss?item=/Search-Box&sc_apikey=' +
      config.sitecoreApiKey;

    const filter = country === 'CAN' ? 'canada' : 'united states';

    return fetch(`${baseUrl}&country=${filter}`)
      .then((res) => res.json())
      .then((res) => {
        try {
          const data = res.sitecore.route.placeholders['jss-main'][0].fields;
          setValues(data);
        } catch (e) {
          console.log(e);
        }
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const handleUserKeyPress = (event) => {
    if (event.target.type === 'text') return;

    const { keyCode } = event;
    if (keyCode === 32) {
      if (videoRef && videoRef.current) {
        if (videoRef.current.paused) {
          videoRef.current.play();
          setPaused(false);
        } else {
          videoRef.current.pause();
          setPaused(true);
        }
        event.preventDefault();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleUserKeyPress);
    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  }, [handleUserKeyPress]);

  useEffect(() => {
    if (videoRef && videoRef.current) {
      /* videoRef.current.addEventListener("loadedmetadata", () => {
        console.log(videoRef.current.buffered);
        if (videoRef.current.buffered && videoRef.current.buffered.length === 0)
          return;

        const bufferedSeconds =
          videoRef.current.buffered.end(0) - videoRef.current.buffered.start(0);
        console.log(`${bufferedSeconds} seconds of video are ready to play.`);
      });*/
      setTimeout(() => {
        if (!isTablet) {
          videoRef.current.play();
          setPaused(false);
        }
      });
    }
    // isTablet derives from the breakpoint, which only resolves after mount.
  }, [videoRef, isTablet]);

  const handlePlayClick = useCallback(() => {
    if (videoRef && videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setPaused(false);
      } else {
        videoRef.current.pause();
        setPaused(true);
      }
    }
  }, [videoRef]);

  useEffect(() => {
    fetchSearchBox(country);
  }, [country]);

  const markets = getFieldValue(values, 'markets');
  const communities = getFieldValue(values, 'communities');
  const plans = getFieldValue(values, 'plans');
  const qmis = getFieldValue(values, 'qmis');

  const videoType = getVideoType(video.href);

  const videoTranscriptBackgroundColor =
    getFieldValue(fields, 'videoTranscriptBackgroundColor') || 'bg-white';
  const videoTrancriptText = getFieldValue(fields, 'videoTrancriptText');
  const videoTranscriptFile =
    getFieldValue(fields, 'videoTranscriptFile') || {};

  return (
    <>
      {videoType === 'internal' && (
        <Helmet>
          <link
            rel='preload'
            as='video'
            href={video.href}
            media='(min-width: 768px)'
            fetchPriority='high'
          />
        </Helmet>
      )}
      {background && (
        <Helmet>
          <link
            rel='preload'
            fetchPriority='high'
            as='image'
            href={background}
          />
        </Helmet>
      )}
      <StyledSection
        id='masthead'
        background={background}
        className={cx(
          'h-90 md:h-84 w-full lg:items-start relative bg-center bg-cover bg-coverbg-no-repeat lg:pb-0 lg:pt-0 z-30',
          videoTranscriptBackgroundColor,
        )}
      >
        <div className='w-full h-full absolute  inset-0 top-0 right-0 left-0 bottom-0 overflow-hidden'>
          {videoType === 'external' && (
            <Video
              as='iframe'
              className='hidden md:block absolute w-full h-full object-cover bg-black'
              src={
                getEmbedUrl(video.href) + '&version=3&enablejsapi=1&controls=0'
              }
            />
          )}
          {videoType === 'internal' && (
            <>
              <Video
                className='hidden md:block absolute inset-0 w-full h-full object-cover'
                autoPlay //={!isTablet}
                muted
                loop
                src={video.href}
                ref={videoRef}
                playsInline
                poster={background}
              />
            </>
          )}
        </div>

        <GradientTop className='absolute inset-0 w-full pointer-events-none' />

        <Container
          className='relative z-10 h-full flex flex-col items-start justify-center lg:w-9/12 md:px-1/16 pointer-events-none'
          ref={containerRef}
        >
          <StyledHeading
            data-aos='fade-up'
            data-aos-anchor='header'
            color='white'
            className='leading-10 md:text-5xl md:mb-0'
            // shadowed
          >
            {title}
          </StyledHeading>
          <StyledCopy
            data-aos='fade-up'
            data-aos-delay='100'
            data-aos-anchor='header'
            color='white'
            className='hidden md:block text-xl lg:mb-8 text-lg'
            // shadowed
          >
            {subtitle}
          </StyledCopy>
          <InputGroup
            data-aos='fade-up'
            data-aos-delay='200'
            data-aos-anchor='header'
            className='z-40 pointer-events-auto'
          >
            <div className='relative flex-grow' ref={ref}>
              <div className='w-full h-full' ref={searchBoxRef}>
                <Input
                  id={'mastheadSeachBox'}
                  onChange={(e) => {
                    setQuery(e.target.value);
                  }}
                  onFocus={() => setSearchBoxOpen(true)}
                  className='w-full h-full rounded'
                  placeholder={placeholder}
                />
                {query.length >= 3 && searchBoxOpen && (
                  <div className='mt-2 absolute'>
                    <SearchBox
                      query={query}
                      width={isMobile ? groupWidth : width}
                      placeholder='Search by current location'
                      markets={markets}
                      communities={communities}
                      plans={plans}
                      qmis={qmis}
                      latLng={latLng}
                      country={country}
                    />
                  </div>
                )}
              </div>
            </div>
          </InputGroup>
          <IconButton
            data-aos='fade-down'
            data-aos-delay='1000'
            data-aos-anchor='header'
            className='absolute left-0 right-0 m-auto bottom-0 lg:mb-5 pointer-events-auto'
            icon='arrow-down'
            idIcon='Masthead'
            circled
            shadowed
            color='white'
            onClick={() => {
              scrollToSection(scrollToSectionField);
            }}
            buttonLabel={'Navigate to more information on this page.'}
            role='link'
          />
        </Container>
        {!isMobile && video && video.href && (
          <PlayPauseButton
            onClick={handlePlayClick}
            paused={paused}
            className={btnPlayPauseClass}
          />
        )}
      </StyledSection>
      {!isMobile &&
        video &&
        video.href &&
        videoTranscriptFile &&
        videoTranscriptFile.link &&
        videoTranscriptFile.link.href && (
          <TranscriptContainer
            className={videoTranscriptBackgroundColor || 'bg-white'}
          >
            <span className='richtext text-left text-md leading-7 font-graphie text-mattamy-blue'>
              {'Transcript: '}
              <a
                href={videoTranscriptFileUrl(videoTranscriptFile)}
                download={
                  videoTranscriptFile.name +
                  videoTranscriptFileExtension(videoTranscriptFile.extension)
                }
                className='underline'
                aria-label={'Download ' + videoTrancriptText + ' transcript'}
              >
                {videoTrancriptText}
              </a>
            </span>
          </TranscriptContainer>
        )}
    </>
  );
};

export default withSitecoreContext()(Masthead);
